import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Dialog,
  Box,
  FormControlLabel,
  Switch,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import { format, set } from "date-fns";
import GenericInfiniteTable, { ColumnDef } from "./GenericInfiniteTable";
import {
  RxDtoProcessingStatusEnum,
  RxTableDto,
  RxTableFilters,
  RxTableFiltersDoctorReportStatusEnum,
} from "../../api-client";
import Controls from "../../atoms/Controls";
import { useInfiniteRxsTable } from "./hooks/usRxTable";
import { useMasterTables } from "../../stores/MasterTables";
import { useLocationId } from "../../stores/User/User.hooks";
import { observer } from "mobx-react";
import useStore from "../../helpers/useStore";
import { NavTablesContext } from "../../stores/NavTables";
import { Search } from "@mui/icons-material";
import { PAGE_SIZE } from "../../stores/NavTables/navTablesStore";
import { getStatusColor } from "../../components/PatientOverview/RxInfo/RxForms/statusColor";
import { therapyRxAmountList } from "../../helpers/therapyRxLabel";

interface RxsTableDialogProps {
  open: boolean;
  onClose: () => void;
  onRxSelect: (patientId: number, rxId: string) => void;
}

const defaultFilters: RxTableFilters = {
  limit: PAGE_SIZE,
  rxNumber: "",
  includeDeleted: false,
  sortBy: { patientName: "ASC" },
  processingStatus: undefined,
  doctorReportStatus: undefined,
  lastName: "",
};

// helper for checking if filters differ from default
const hasFilters = (filters: RxTableFilters): boolean => {
  let hasFilters = false;
  for (const key in defaultFilters) {
    if (key === "lastName" || key === "location") {
      continue; // skip these keys
    }
    if (
      (filters as any)[key] &&
      JSON.stringify((filters as any)[key]) != JSON.stringify((defaultFilters as any)[key])
    ) {
      // console.debug(`Filter ${key} differs from default:`, toJS((filters as any)[key]), " vs ", (defaultFilters as any)[key]);
      hasFilters = true;
      break;
    }
  }
  return hasFilters;
};

const RxsTableDialog = observer(({ open, onClose, onRxSelect }: RxsTableDialogProps) => {
  const locationId = useLocationId();
  const { data: masterTablesData } = useMasterTables();

  const NavTableStore = useStore(NavTablesContext);
  const {
    rxNavTableParams,
    setIncomingRx,
    setRxTableFilters: setFilters,
    rxNavTableSelectedIndex: selectedIndex,
    setRxNavTableSelectedIndex: setSelectedIndex,
  } = NavTableStore;
  const filters = rxNavTableParams.rxTableFilters;
  const { patientLastName, incomingRxId } = rxNavTableParams;
  const [dialogOutsidePagination, setDialogOutsidePagination] = useState(false);

  // ---- Location dropdown ----
  const locationsOptions =
    (masterTablesData || [])
      .filter((obj: any) => "location" in obj)
      .map((obj: any) =>
        obj.location?.map((l: any) => ({
          label: l.name?.toString(),
          value: l.id?.toString(),
        }))
      )?.[0] || [];

  useEffect(() => {
    if (locationId) {
      setFilters((prev) => ({ ...prev, location: locationId }));
    }
  }, [locationId]);

  const handleFilterChange = (field: keyof RxTableFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value !== "" ? value : undefined,
      rxId: undefined, // clear rxId on any filter change
    }));
  };

  // ---- handle multi-column sorting ----
  const handleSortChange = (columnKey: string) => {
    setFilters((prev) => {
      type SortByType = Partial<Record<string, "ASC" | "DESC">>;
      const currentSort: SortByType = prev.sortBy || {};
      const currentDirection = currentSort[columnKey as string];

      const newDirection =
        currentDirection === "ASC" ? "DESC" : currentDirection === "DESC" ? undefined : "ASC";

      const updatedSort: SortByType = { ...currentSort };

      if (!newDirection) {
        delete updatedSort[columnKey as string];
      } else {
        updatedSort[columnKey as string] = newDirection;
      }

      return {
        ...prev,
        sortBy: updatedSort,
      };
    });
  };

  // adjust filters based on incomingRxId
  // console.log(
  //   "incomingRxId",
  //   incomingRxId,
  //   " hasFilters? ",
  //   hasFilters(filters),
  //   filters,
  //   defaultFilters
  // );

  useEffect(() => {
    if (!incomingRxId) return;

    // Only act if lastName is not set and there are no other filters
    if (!filters.lastName && !hasFilters(filters)) {
      const updatedFilters = { ...filters, lastName: patientLastName || "" };
      setFilters(updatedFilters, false); // this is the special case where we do not want to clear incomingRxId
    }
  }, [incomingRxId, patientLastName]);

  // ---- Memoized filters ----
  const memoizedFilters = useMemo(() => ({ ...filters }), [filters]);

  // ---- Data fetching ----
  const {
    data,
    isFetchingNextPage,
    hasNextPage: hasMoreData,
    fetchNextPage,
    isLoading,
  } = useInfiniteRxsTable({
    filters: memoizedFilters,
    enabled: open && !!locationId,
  });

  // const rxs: RxTableDto[] = data?.pages.flat() ?? [];
  const [rxs, setRxs] = useState<RxTableDto[]>(data?.pages.flat() ?? []);
  useEffect(() => {
    setRxs(data?.pages.flat() ?? []);
  }, [data]);

  const handleWarnCloseDialog = () => {
    setDialogOutsidePagination(false);
  };

  // Whenever filters change → clear selection
  // useEffect(() => {
  //   setSelectedIndex(-1);
  // }, [filters]);

  // useEffect(() => {
  //   if (!open || !rxs.length) return;
  //   if (!selectedRxId) return;

  //   const index = rxs.findIndex((r) => r.id === selectedRxId);

  //   const rxIdChanged = lastHandledRxId.current !== selectedRxId;
  //   const nothingSelected = selectedIndex == null || selectedIndex === -1;

  //   const shouldAutoselect = rxIdChanged || nothingSelected;

  //   if (index >= 0) {
  //     if (shouldAutoselect) {
  //       setSelectedIndex(index);
  //       hasShownMissingRxDialog.current = false;
  //     }
  //     lastHandledRxId.current = selectedRxId;
  //   } else if (!hasShownMissingRxDialog.current) {

  //     setDialogOutsidePagination(true);
  //     hasShownMissingRxDialog.current = true;
  //   }
  // }, [open, rxs, selectedRxId, selectedIndex]);
  useEffect(() => {
    if (!open || !incomingRxId || !rxs.length) return;

    // Find the current index of the selected Rx in the latest rxs
    if (filters.lastName == patientLastName || !filters.lastName) {
      const index = rxs.findIndex((r) => r.id === incomingRxId);

      if (index >= 0) {
        // Rx exists in the current table → select it
        if (selectedIndex != index) {
          setSelectedIndex(index);
          setDialogOutsidePagination(false);
        }
      } else {
        // Rx not found → show warning
        if (hasFilters(filters)) {
          setDialogOutsidePagination(true);
        }
      }
    }
  }, [open, incomingRxId, rxs]);

  const handleClose = () => {
    onClose();
  };

  // ---- Table columns ----
  const columns: ColumnDef<RxTableDto>[] = useMemo(
    () => [
      {
        key: "rxNumber",
        header: "Rezeptnummer",
        render: (r) => r.rxNumber,
        sortable: true,
      },
      {
        key: "patientName",
        header: "Name (Nachname, Vorname)",
        render: (r) =>
          `${(r.patient as any)?.lastName ?? "-"}, ${(r.patient as any)?.firstName ?? "-"}`,
        sortable: true,
      },
      {
        key: "firstAppointmentDate",
        header: "Erster Termin",
        render: (r) =>
          r.firstAppointmentDate ? format(new Date(r.firstAppointmentDate), "dd.MM.yyyy") : "-",
        sortable: true,
      },
      {
        key: "therapyRxs",
        header: "Anzahl Therapien",
        render: (r) => therapyRxAmountList(r.therapyRxs, ",  "),
      },
      {
        key: "date",
        header: "Rezeptdatum",
        render: (r) => (r.date ? format(new Date(r.date), "dd.MM.yyyy") : "-"),
        sortable: true,
      },
      {
        key: "payer",
        header: "Versicherungsgesellschaft",
        render: (r) => r.payer?.name ?? "-",
      },
      {
        key: "insuranceNo",
        header: "Versicherungsnummer",
        render: (r) => r.insureeNumber ?? "-",
      },
      {
        key: "doctor",
        header: "Arzt",
        render: (r) =>
          `${r.doctor?.contactInfo?.lastName ?? "-"}, ${r.doctor?.contactInfo?.firstNames ?? "-"}`,
      },
      {
        key: "doctorReportStatus",
        header: "Berichtsstatus",
        render: (r) => (r.doctorReports as any)?.status ?? "-",
      },
      {
        key: "processingStatus",
        header: "Status",
        render: (r) => r.processingStatus ?? "-",
      },
    ],
    []
  );
  const handleRxSelect = (rx: RxTableDto) => {
    onRxSelect((rx?.patient! as any)?.id!, rx?.id);
  };
  const handleSearchStringChange = (newString: string | ((prevString: string) => string)) => {
    setFilters((prev) => ({
      ...prev,
      lastName: typeof newString === "function" ? newString(prev.lastName || "") : newString,
    }));
  };
  // ---- Toolbar ----
  const toolbar = (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
      {/* Location Filter */}
      <Controls.Select
        name="location"
        label={<Typography sx={{ fontSize: "1rem" }}>Standort</Typography>}
        value={filters.location ?? ""}
        onChange={(e: any) => handleFilterChange("location", e.target.value)}
        options={locationsOptions}
        style={{ width: 200 }}
      />

      {/* Last Name Search */}
      <TextField
        label={<Typography sx={{ fontSize: "1rem" }}>Nachname</Typography>}
        variant="outlined"
        size="small"
        value={filters.lastName ?? ""}
        onChange={(e) => handleSearchStringChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search color="action" />
            </InputAdornment>
          ),
        }}
      />

      {/* Processing Status Filter */}
      {/* <Controls.Select
        name="processingStatus"
        label="Berarbeitungsstatus"
        value={filters?.processingStatus}
        onChange={(e: any) => handleFilterChange("processingStatus", e.target.value)}
        options={Object.values(RxDtoProcessingStatusEnum).map((value) => ({
          value,
          label: value,
        }))}
        noTriggerChange={true}
        style={{ width: 200 }}
      /> */}
      <Autocomplete
        options={Object.values(RxDtoProcessingStatusEnum)}
        value={filters.processingStatus ?? null}
        onChange={(_, value) => {
          handleFilterChange("processingStatus", value ?? undefined);
        }}
        getOptionLabel={(option) => option}
        renderOption={(props, option) => {
          const color = getStatusColor(option);
          return (
            <Box component="li" {...props} sx={{ color, fontWeight: 500 }}>
              {option}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Bearbeitungsstatus"
            size="small"
            InputProps={{
              ...params.InputProps,
              sx: {
                color: getStatusColor(filters.processingStatus),
                fontWeight: 500,
              },
            }}
          />
        )}
        sx={{ width: 200 }}
      />

      {/* Doctor Report Status Filter */}
      <Autocomplete
        options={Object.values(RxTableFiltersDoctorReportStatusEnum)}
        value={filters.doctorReportStatus ?? null}
        onChange={(_, value) => {
          handleFilterChange("doctorReportStatus", value ?? undefined);
        }}
        renderInput={(params) => <TextField {...params} label="Berichtsstatus" size="small" />}
        sx={{ width: 200 }}
      />

      {/* Show Deleted Switch */}
      <FormControlLabel
        control={
          <Switch
            checked={filters.includeDeleted ?? false}
            onChange={(e) => handleFilterChange("includeDeleted", e.target.checked)}
            color="primary"
          />
        }
        label={<Typography sx={{ fontSize: "1rem" }}>Gelöschte anzeigen</Typography>}
      />
    </Box>
  );

  return (
    <>
      <Dialog fullScreen open={open} onClose={handleClose}>
        <DialogTitle sx={{ p: 2, fontSize: "1.5rem" }}>Rezepte</DialogTitle>
        <DialogContent>
          <GenericInfiniteTable
            data={rxs}
            columns={columns}
            hasMoreData={hasMoreData}
            fetchMoreData={fetchNextPage}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            onRowClick={handleRxSelect}
            toolbar={toolbar}
            onSortChange={handleSortChange}
            sortBy={filters.sortBy as any} // now an object
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            onSearchStringChange={handleSearchStringChange}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="text" onClick={handleClose}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={dialogOutsidePagination} onClose={handleWarnCloseDialog}>
        <DialogTitle>Rezept nicht gefunden</DialogTitle>
        <DialogContent>
          Das ausgewählte Rezept liegt außerhalb der aktuellen Filter. Möchtest du die Filter
          verwerfen und das neu gewählte Rezept laden?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarnCloseDialog}>Nein</Button>
          <Button
            onClick={() => {
              handleWarnCloseDialog(); // closes the warning dialog
              // setPendingRxId(selectedRxId!);
              setFilters({
                ...defaultFilters,
                lastName: patientLastName || "", // pre-fill with patient last name
              });
            }}
          >
            Ja, Filter zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default RxsTableDialog;
